// src/layouts/AppLayout.tsx
import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const isActive = (path: string): boolean => location.pathname.startsWith(path);

  const handleLogout = async () => {
    await logout();
    setIsMobileNavOpen(false);
  };

  const handleNavigate = () => {
    setIsMobileNavOpen(false);
  };

  const NavLinks: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
    <nav className="app-sidebar-nav">
      <Link
        to="/app/dashboard"
        className={
          isActive("/app/dashboard") || location.pathname === "/app"
            ? "active"
            : undefined
        }
        onClick={onNavigate}
      >
        {t("appLayout.nav.dashboard")}
      </Link>

      <Link
        to="/app/bots"
        className={isActive("/app/bots") ? "active" : undefined}
        onClick={onNavigate}
      >
        {t("appLayout.nav.bots")}
      </Link>

      <Link
        to="/app/billing"
        className={isActive("/app/billing") ? "active" : undefined}
        onClick={onNavigate}
      >
        {t("appLayout.nav.billing")}
      </Link>

      <Link
        to="/app/account"
        className={isActive("/app/account") ? "active" : undefined}
        onClick={onNavigate}
      >
        {t("appLayout.nav.profile")}
      </Link>

      {/* Role-based sections */}
      {user?.role === "REFERRER" && (
        <>
          <div className="app-nav-separator" />
          <div className="app-nav-section-title">Referrals</div>
          <Link
            to="/app/referrals"
            className={isActive("/app/referrals") ? "active" : undefined}
            onClick={onNavigate}
          >
            Referrer panel
          </Link>
        </>
      )}

      {user?.role === "ADMIN" && (
        <>
          <div className="app-nav-separator" />
          <div className="app-nav-section-title">Admin</div>
          <Link
            to="/app/admin/users"
            className={isActive("/app/admin/users") ? "active" : undefined}
            onClick={onNavigate}
          >
            Users
          </Link>
          <Link
            to="/app/admin/referrals"
            className={isActive("/app/admin/referrals") ? "active" : undefined}
            onClick={onNavigate}
          >
            Referrals
          </Link>
          <Link
            to="/app/admin/bots"
            className={isActive("/app/admin/bots") ? "active" : undefined}
            onClick={onNavigate}
          >
            Bots & subscriptions
          </Link>
          <Link
            to="/app/admin/bookings"
            className={isActive("/app/admin/bookings") ? "active" : undefined}
            onClick={onNavigate}
          >
            Bookings
          </Link>
          <Link
            to="/app/admin/emails"
            className={isActive("/app/admin/emails") ? "active" : undefined}
            onClick={onNavigate}
          >
            Email usage
          </Link>
          <Link
            to="/app/admin/openai-usage"
            className={isActive("/app/admin/openai-usage") ? "active" : undefined}
            onClick={onNavigate}
          >
            OpenAI usage
          </Link>

          <Link
            to="/app/admin/payments"
            className={isActive("/app/admin/payments") ? "active" : undefined}
            onClick={onNavigate}
          >
            Payments
          </Link>
          <Link
            to="/app/admin/integrations"
            className={isActive("/app/admin/integrations") ? "active" : undefined}
            onClick={onNavigate}
          >
            Channels
          </Link>
          <Link
            to="/app/admin/plans"
            className={isActive("/app/admin/plans") ? "active" : undefined}
            onClick={onNavigate}
          >
            Plans & pricing
          </Link>

          {/* Admins can also access referrer portal view */}
          <Link
            to="/app/referrals"
            className={isActive("/app/referrals") ? "active" : undefined}
            onClick={onNavigate}
          >
            Referrer panel
          </Link>
        </>
      )}
    </nav>
  );

  return (
    <div className="app-layout-root">
      {/* DESKTOP SIDEBAR */}
      <aside className="app-sidebar">
        <div className="app-sidebar-header">
          <h2>{t("appLayout.sidebarTitle")}</h2>
        </div>
        <NavLinks />
      </aside>

      <div className="app-main">
        {/* TOP BAR (desktop + mobile) */}
        <header className="app-main-header">
          <div className="app-main-header-left">
            <button
              type="button"
              className="app-burger"
              onClick={() => setIsMobileNavOpen(true)}
              aria-label={t("appLayout.aria.openNav")}
            >
              <span className="app-burger-icon" />
            </button>
            <span className="brand-small">{t("appLayout.brandSmall")}</span>
          </div>

          <div className="app-main-header-right">
            {user && (
              <button className="btn-link" onClick={handleLogout}>
                {t("appLayout.logout")}
              </button>
            )}
          </div>
        </header>

        {/* MOBILE SLIDE-IN NAV */}
        {isMobileNavOpen && (
          <div
            className="app-mobile-nav-overlay"
            onClick={() => setIsMobileNavOpen(false)}
          >
            <aside className="app-mobile-nav" onClick={(e) => e.stopPropagation()}>
              <div className="app-mobile-nav-header">
                <span className="brand-small">{t("appLayout.sidebarTitle")}</span>
                <button
                  type="button"
                  className="app-mobile-nav-close"
                  onClick={() => setIsMobileNavOpen(false)}
                  aria-label={t("appLayout.aria.closeNav")}
                >
                  ×
                </button>
              </div>

              <NavLinks onNavigate={handleNavigate} />

              {user && (
                <div className="app-mobile-nav-user">
                  <div className="app-mobile-nav-email">{user.email}</div>
                  <button
                    type="button"
                    className="btn-link danger"
                    onClick={handleLogout}
                  >
                    {t("appLayout.logout")}
                  </button>
                </div>
              )}
            </aside>
          </div>
        )}

        <main className="app-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
